import './App.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { UserSelectionPage } from './pages/UserSelector.jsx';
import useUserStore from './store/UserStore.js';
import { DadesGenerals } from './pages/DadesGenerals.jsx';
import { Assistència } from './pages/Assistència.jsx';
import { Recibos } from './pages/Recibos.jsx';
import { Noticias } from './pages/Noticias.jsx';
import { Evaluacion } from './pages/Evaluacion.jsx';
import { Contacta } from './pages/Contacta.jsx';
import { Calendario } from './pages/Calendario.jsx';
import { OnTrobarnos } from './pages/OnTrobarnos.jsx';
import { PasswordRecovery } from './pages/PasswordRecovery.jsx';
import { PasswordReset } from './pages/PasswordReset.jsx';
import { Comunicados } from './pages/Comunicados.jsx';
import { createTheme, responsiveFontSizes, ThemeProvider } from '@mui/material/styles';
import { saveUserAction } from './services/saveUserAction.js';
import { Recursos } from './pages/Recursos.jsx';
import Welcome from './pages/Welcome.jsx';

const AccessTracker = () => {
  const location = useLocation();
  const user = useUserStore(state => state.user);

  useEffect(() => {
    saveUserAction({ email: user.email, id_accion: 2, detalles_accion: `Accessed ${location.pathname}` });
  }, [location, user]);

  return null;
};

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const setUser = useUserStore(state => state.setUser);
  const [forbidden, setForbidden] = useState(false);

  let theme = createTheme({
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
    },
  });
  theme = responsiveFontSizes(theme);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    const ENV = import.meta.env.VITE_APP_ENV;

    if (ENV === 'production' && !userAgent.includes("es.stp.stptraining")) {
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        const anchor = document.createElement('a');
        anchor.href = "stptraining://alumnos.stp.es" + window.location.pathname;
        anchor.click();
        setForbidden({
          state: 'redirect',
        });
      } else if (ENV === 'production' && !window.location.href.includes("/reset-password")) {
        setForbidden({
          state: 'forbidden',
        });
      } else {
        setForbidden({
          state: 'none',
        });
      }
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_APP_API_URL}protected`, { withCredentials: true });
        setIsAuthenticated(true);
        setUser({ idpersona: response.data.user.id, email: response.data.user.email, password: "" });
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);


  return (
    forbidden.state === 'forbidden' ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '100%',
          minHeight: '100vh',
        }}
      >
        <h1>Forbidden</h1>
      </Box>
    ) : forbidden.state === 'redirect' ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '100%',
          minHeight: '100vh',
        }}
      >
        <h1>Redirecting to app</h1>
      </Box>
    ) : (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: '100%',
            minHeight: '100vh',
          }}
        >
          {isAuthenticated === null ? (
            <CircularProgress />
          ) : (
            <Router>
              <AccessTracker />
              <Routes>
                <Route
                  path="/"
                  element={isAuthenticated ? <Navigate to="/users" /> : <Navigate to="/welcome" />}
                />
                <Route
                  path="/welcome"
                  element={<Welcome setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/login"
                  element={isAuthenticated ? <Navigate to="/users" /> : <Login setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/users"
                  element={isAuthenticated ? <UserSelectionPage setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/home"
                  element={isAuthenticated ? <Home setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/general-data"
                  element={isAuthenticated ? <DadesGenerals setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/attendance"
                  element={isAuthenticated ? <Assistència setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/receipts"
                  element={isAuthenticated ? <Recibos setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/news"
                  element={<Noticias setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/evaluation"
                  element={isAuthenticated ? <Evaluacion setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/contact"
                  element={<Contacta setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/calendar"
                  element={isAuthenticated ? <Calendario setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/location"
                  element={<OnTrobarnos setIsAuthenticated={setIsAuthenticated} />}
                />
                <Route
                  path="/recursos"
                  element={isAuthenticated ? <Recursos setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="/password-recovery"
                  element={<PasswordRecovery />}
                />
                <Route
                  path="/reset-password/:token"
                  element={<PasswordReset />}
                />
                <Route
                  path="/communications"
                  element={isAuthenticated ? <Comunicados setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/login" />}
                />
                <Route
                  path="*"
                  element={<Navigate to="/login" />}
                />
              </Routes>
            </Router>
          )}
        </Box>
      </ThemeProvider>
    )
  );
}
